import {Injectable} from '@angular/core';
import {NbComponentType, NbWindowService} from '@nebular/theme';
import {productUrl} from '../../shared/routing/url-builder';
import {
  AvailabilityType,
  CartProduct,
  CustomerGroup,
  FileService,
  Picture,
  ProductCustomer,
  ProductStatuses,
  StoreService,
  WarehouseGroup
} from 'compob2b-libs';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  noPhotoSrc = '/assets/images/product/nofoto.png';

  readonly customerGroup$ = this.storeService.getCustomerGroup();
  warehouseGroups: WarehouseGroup[] = [];
  customerGroup: CustomerGroup;
  deliveryDays: number;

  constructor(
    readonly storeService: StoreService,
    readonly fileService: FileService,
    readonly windowService: NbWindowService,
  ) {
  }

  handleCustomerGroup(customerGroup: CustomerGroup) {
    if (customerGroup) {
      this.customerGroup = customerGroup;
      this.warehouseGroups = customerGroup.warehouseGroup;
      this.initDeliveryDays();
    }
  }

  getImageUri(picture: Picture) {
    return picture ? this.fileService.getImageUri(picture, 200) : this.noPhotoSrc;
  }

  /**
   * URL карточки товара для атрибута href. `encodeURIComponent` обязателен —
   * `row.url` может содержать кириллицу/спецсимволы, без кодирования href
   * будет malformed и ctrl-click сломается. Раньше использовали `routerLink`
   * который кодировал через UrlSerializer; сейчас `[href]` (см. CLAUDE.md
   * SSR rules — клик на ссылку, открывающую модалку, должен быть `[href]`,
   * иначе RouterLink навигейтит независимо от `event.preventDefault()`).
   */
  productHref(product: { url?: string | null; syncUid?: string; header?: string }): string {
    if (!product.url && !product.syncUid) {
      // Без идентификатора href невалиден; '#' не вызывает full reload и
      // (click) handler в template может префиксить event.preventDefault().
      return '#';
    }
    // Форму URL знает ТОЛЬКО билдер (ТЗ §6.3): здесь её больше не собираем, иначе
    // резолвер §6.4 сравнивал бы фактический путь с другой реализацией и гонял 301
    // по кругу. `header` может отсутствовать у урезанных проекций товара — тогда
    // получится путь без slug, который резолвер дозаполнит.
    return productUrl({
      url: product.url,
      syncUid: product.syncUid ?? '',
      header: product.header ?? '',
    });
  }

  classAvailability(product: ProductCustomer | CartProduct): string {
    let index = 0;
    if (product.availabilityOnGroupsOfWarehouses) {
      Object.keys(product.availabilityOnGroupsOfWarehouses).forEach(e => {
        const type = product.availabilityOnGroupsOfWarehouses[e];
        const i = parseInt(AvailabilityType[type.valueOf()], 10);
        if (i > index) {
          index = i;
        }
      });
    }

    return 'available' + index;
  }

  countAvailability(product: ProductCustomer | CartProduct): number {
    const stocks = product.balancesOnGroupsOfWarehouses;
    if (!stocks) {
      return 0;
    }

    let sum = 0;
    this.warehouseGroups?.forEach(g => {
      if (g.header !== 'Поставщик' && stocks[g.id] !== undefined) {
        sum += stocks[g.id];
      }
    });
    if (isNaN(sum)) {
      return 0;
    }
    return sum;
  }

  getColumnId(column: string): number {
    return parseInt(column.split('.').pop());
  }

  getWarehouseInfo(column: string, product: ProductCustomer | CartProduct) {
    let qnt = 0;
    const stocks = product.balancesOnGroupsOfWarehouses;

    if (stocks) {
      const warehouseGroupId = this.getColumnId(column);
      if (stocks[warehouseGroupId]) {
        qnt = stocks[warehouseGroupId];
      }
    }

    return qnt || 0;
  }

  openModalProduct(type: NbComponentType, syncUid: string, productList: ProductCustomer[] = []): void {
    if (syncUid) {
      this.storeService.nextCurrentProduct(syncUid);
      this.windowService.open(type, {
        context: {
          productUid: syncUid,
          productList: productList,
        },
        closeOnEsc: false
      });
    }
  }

  /**
   * Лениво грузит ModalProductComponent и открывает его. Динамический import —
   * единственная точка зависимости от modal-product; держит разорванным цикл
   * product-view → product-tabs → product-list → modal-product (extends product-view).
   * Зовётся из product-list/search-autocomplete вместо статического импорта.
   */
  openModalProductLazy(syncUid: string, productList: ProductCustomer[] = []): void {
    import('../modal-product/modal-product.component')
      .then(({ModalProductComponent}) => this.openModalProduct(ModalProductComponent, syncUid, productList));
  }

  outOfStockAndHasAnalogs(cartProduct: CartProduct): boolean {
    return cartProduct.analogCount > 0 && this.countAvailability(cartProduct) < cartProduct.amount;
  }

  isOutOfProduction(cartProduct: CartProduct) {
    return cartProduct.productId !== null && 'productStatusId' in cartProduct &&
      cartProduct.productStatusId === ProductStatuses.OUT_OF_PRODUCTION &&
      cartProduct.amount > this.countAvailability(cartProduct);
  }

  isOutOfMultiplicity(cartProduct: CartProduct) {
    return cartProduct.productId !== null && cartProduct.amount > this.countAvailability(cartProduct) &&
      (cartProduct.amount < cartProduct.multiplicitySupplier || cartProduct.amount % cartProduct.multiplicitySupplier !== 0);
  }

  getAvailability(column: string, product: ProductCustomer | CartProduct): string {
    if (product.availabilityOnGroupsOfWarehouses) {
      const warehouseGroupId = this.getColumnId(column);
      const availability: AvailabilityType = product.availabilityOnGroupsOfWarehouses[warehouseGroupId];
      return availability === undefined ? 'available0' : 'available' + AvailabilityType[availability.valueOf()];
    }

    return 'available0';
  }

  countRegionAvailability(cartProduct: CartProduct): number {
    const stocks = cartProduct.balancesOnGroupsOfWarehouses;
    if (stocks === null) {
      return 0;
    }

    let sum = 0;
    this.warehouseGroups?.forEach(g => {
      if (!g.base && stocks[g.id] !== undefined) {
        sum += stocks[g.id];
      }
    });

    if (isNaN(sum)) {
      return 0;
    }
    return sum;
  }

  initDeliveryDays(): void {
    let d = 0;
    this.warehouseGroups?.forEach(g => {
      if (!g.base && g.deliveryDays > d) {
        d = g.deliveryDays;
      }
    });
    this.deliveryDays = d;
  }

  countSupplyAvailability(product: ProductCustomer | CartProduct): number {
    const stocks = product.balancesOnGroupsOfWarehouses;
    if (!stocks) {
      return 0;
    }

    let sum = 0;
    this.warehouseGroups?.forEach(g => {
      if (g.header === 'Поставщик' && stocks[g.id] !== undefined) {
        sum += stocks[g.id];
      }
    });

    if (isNaN(sum)) {
      return 0;
    }
    return sum;
  }

  getSupplyTerm(row: CartProduct) {
    const balances = row.balancesOnGroupsOfWarehouses;
    const inStock = balances == null ? 0 : this.countAvailability(row);
    const inSupplyStock = balances == null ? 0 : this.countSupplyAvailability(row);
    const inRegionStock = balances == null ? 0 : this.countRegionAvailability(row);

    const supplierDeliveryTime = row.supplyTerm === 0 ? 1 : row.supplyTerm;

    if (inStock === 0 && inSupplyStock === 0) {
      return '-';
    }
    if (row.amount > inStock + inSupplyStock) {
      return '-';
    }

    if (this.customerGroup?.header === 'Москва') {
      if (inStock === 0 && inSupplyStock > 0) {
        return supplierDeliveryTime;
      }
      return row.amount > inStock ? supplierDeliveryTime : 1;
    }
    else {
      if (inRegionStock > 0) {
        return 1;
      }
      if (inRegionStock === 0 && inStock > 0) {
        return 1 + this.deliveryDays;
      }
      return supplierDeliveryTime + this.deliveryDays;
    }

  }

}
